import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface BrandingData {
  platformName: string;
  primaryColor: string;
  accentColor: string;
  logoUrl: string | null;
  welcomeMessage: string;
  loaded: boolean;
}

const defaultBranding: BrandingData = {
  platformName: "Thiia",
  primaryColor: "#7B2D8E",
  accentColor: "#B39DDB",
  logoUrl: null,
  welcomeMessage: "Bienvenue sur votre espace professionnel",
  loaded: false,
};

const BrandingContext = createContext<BrandingData>(defaultBranding);

function hexToHSL(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { user, isCoach } = useAuth();
  const [branding, setBranding] = useState<BrandingData>(defaultBranding);

  useEffect(() => {
    if (!user || !isCoach) {
      setBranding({ ...defaultBranding, loaded: true });
      return;
    }

    const fetchBranding = async () => {
      // Find the owner via owner_coaches
      const { data: link } = await supabase
        .from("owner_coaches")
        .select("owner_id")
        .eq("coach_id", user.id)
        .limit(1)
        .maybeSingle();

      if (!link) {
        setBranding({ ...defaultBranding, loaded: true });
        return;
      }

      const { data } = await supabase
        .from("owner_branding")
        .select("*")
        .eq("owner_id", link.owner_id)
        .maybeSingle();

      if (data) {
        const b: BrandingData = {
          platformName: data.platform_name,
          primaryColor: data.primary_color,
          accentColor: data.accent_color,
          logoUrl: data.logo_url,
          welcomeMessage: data.welcome_message ?? defaultBranding.welcomeMessage,
          loaded: true,
        };
        setBranding(b);

        // Apply CSS custom properties for theming
        const root = document.documentElement;
        root.style.setProperty("--primary", hexToHSL(data.primary_color));
        root.style.setProperty("--accent", hexToHSL(data.accent_color));
        root.style.setProperty("--ring", hexToHSL(data.primary_color));

        // Sidebar colors derived from primary
        const primaryHSL = hexToHSL(data.primary_color);
        const [h] = primaryHSL.split(" ");
        root.style.setProperty("--sidebar-background", `${h} 30% 14%`);
        root.style.setProperty("--sidebar-primary", `${h} 56% 67%`);
        root.style.setProperty("--sidebar-accent", `${h} 20% 22%`);
        root.style.setProperty("--sidebar-border", `${h} 20% 22%`);
        root.style.setProperty("--sidebar-ring", `${h} 56% 67%`);
      } else {
        setBranding({ ...defaultBranding, loaded: true });
      }
    };

    fetchBranding();

    // Cleanup: reset CSS vars on unmount
    return () => {
      const root = document.documentElement;
      root.style.removeProperty("--primary");
      root.style.removeProperty("--accent");
      root.style.removeProperty("--ring");
      root.style.removeProperty("--sidebar-background");
      root.style.removeProperty("--sidebar-primary");
      root.style.removeProperty("--sidebar-accent");
      root.style.removeProperty("--sidebar-border");
      root.style.removeProperty("--sidebar-ring");
    };
  }, [user, isCoach]);

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
